import React, { useState } from 'react';
import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import { Search, ChevronRight, Menu, X, ArrowLeft } from 'lucide-react';
import { GUIDE_CATEGORIES } from '@/data/guides';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const HelpLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  // Simple search logic
  const filteredCategories = GUIDE_CATEGORIES.map(cat => ({
    ...cat,
    articles: cat.articles.filter(art => 
      art.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      art.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.articles.length > 0);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Toggle */}
      <button 
        className="lg:hidden fixed bottom-6 right-6 z-50 w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-40 w-72 bg-card border-r border-border transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:block",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-full flex flex-col p-6 overflow-y-auto">
          <div className="mb-8">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-xs font-bold uppercase tracking-widest transition-colors">
              <ArrowLeft className="w-3 h-3" />
              Back to Dashboard
            </Link>
            <h1 className="text-xl font-black tracking-tight mb-2 uppercase">Help Center</h1>
            <p className="text-xs text-muted-foreground mb-6">Expert guide for every setting.</p>
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search settings..." 
                className="pl-9 h-10 bg-muted/50 border-none text-sm rounded-xl focus-visible:ring-1 focus-visible:ring-primary"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <nav className="space-y-8">
            {filteredCategories.map((category) => (
              <div key={category.id}>
                <div className="flex items-center gap-2 text-[10px] uppercase font-black tracking-widest text-muted-foreground/60 mb-3 px-2">
                  <category.icon className="w-3 h-3" />
                  {category.title}
                </div>
                <div className="space-y-1">
                  {category.articles.map((article) => (
                    <NavLink
                      key={article.slug}
                      to={`/help/${category.id}/${article.slug}`}
                      onClick={() => setIsSidebarOpen(false)}
                      className={({ isActive }: { isActive: boolean }) => cn(
                        "flex items-center justify-between group px-3 py-2.5 rounded-xl text-sm transition-all duration-200",
                        isActive 
                          ? "bg-primary text-primary-foreground font-medium shadow-sm shadow-primary/20" 
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                    >
                      {({ isActive }: { isActive: boolean }) => (
                        <>
                          <span className="truncate">{article.title}</span>
                          <ChevronRight className={cn("w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity", isActive && "opacity-0")} />
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-grow min-w-0 bg-background lg:max-h-screen lg:overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-12 lg:px-12 lg:py-16">
          <Outlet />
        </div>
      </main>

      {/* Backdrop */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default HelpLayout;
